import React, { useEffect, useMemo, useState } from 'react';

import { Button, Grid, makeStyles, Typography } from '@material-ui/core';

import { DOCS, IMAGE, VIDEO, DCM, PDF } from '../../models/Resource';
import { documentFormats, imageFormats, videoFormats } from '../resources/AddResource';

const useStyles = makeStyles(() => {
  return {
    fileContainer: {
      lineBreak: 'anywhere',
      maxWidth: '50%',
      width: 'calc(50% - 16px)',
    },
    filesContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    statsContainer: {
      background: '#F5F5F5',
      borderRadius: '10px',
      fontSize: '11px',
      gap: '16px',
      lineBreak: 'anywhere',
      padding: '7px 14px',
      textAlign: 'left',
    },
    statsText: {
      fontSize: '11px',
      fontWeight: 'bold',
      lineBreak: 'anywhere',
    },
    uploadActions: {
      color: '#8C8C8C',
      display: 'flex',
      fontSize: '12px',
      fontWeight: '400',
      marginTop: '1rem',
      width: 'fit-content',
    },
  };
});

export default ({
  onUploadedFiles,
  onOpenUploadReportDialog,
  onUploadedReport,
  imageFileType,
  radiologyReportRequired,
  onOpenSelectImageUpload,
  status,
  onRemoveFiles,
  mode,
  onRemoveReport,
}) => {
  const classes = useStyles();
  const [selectedType, setSelectedType] = useState(DCM);

  const handleReportChange = (evt) => {
    if (onUploadedReport) onUploadedReport([...evt.target.files]);
    // eslint-disable-next-line no-param-reassign
    evt.target.value = null;
  };

  const handleFileChange = (evt, type) => {
    setSelectedType(type);
    if (onUploadedFiles) onUploadedFiles([...evt.target.files], type);
    // eslint-disable-next-line no-param-reassign
    evt.target.value = null;
  };

  useEffect(() => {
    setSelectedType(status.fileType);
  }, [status]);

  const accept = useMemo(() => {
    if (selectedType === IMAGE) {
      return imageFormats.map((e) => `.${e}`).join(',');
    }

    if (selectedType === VIDEO) {
      return videoFormats.map((e) => `.${e}`).join(',');
    }
    if (selectedType === DOCS || selectedType === PDF) {
      return documentFormats.map((e) => `.${e}`).join(',');
    }

    return '';
  }, [selectedType]);

  const renderUploadField = () => {
    const renderChangeFilesButton = () => {
      return (
        <>
          <input
            onChange={(event) => handleFileChange(event, selectedType)}
            multiple={selectedType === DCM ? '' : null}
            directory={selectedType === DCM ? '' : null}
            webkitdirectory={selectedType === DCM ? '' : null}
            moxdirectory={selectedType === DCM ? '' : null}
            accept={accept}
            hidden
            id="raised-button-change-images"
            type="file"
          />
          <label htmlFor="raised-button-change-images">
            <span style={{ color: '#2AAAE2', cursor: 'pointer' }}>Change</span>
          </label>
          &nbsp; | &nbsp;
          <span onClick={onRemoveFiles} style={{ color: '#B12A2A', cursor: 'pointer' }} aria-hidden="true">
            Remove
          </span>
        </>
      );
    };

    if (status.uploaded) {
      return (
        <div className={classes.filesContainer}>
          <Grid container className={classes.statsContainer}>
            <Grid item className={classes.fileContainer}>
              <Typography className={classes.statsText}>Title</Typography>
              <Typography className={classes.statsText}> {status.study} </Typography>
            </Grid>
            <Grid item className={classes.fileContainer}>
              <Typography className={classes.statsText}>{status.fileType === DCM ? 'Images' : 'File Type'}</Typography>
              <Typography className={classes.statsText} style={{ color: status.imageCount > 0 ? '#388E3C' : 'B12A2A' }}>
                {status.fileType === DCM ? `${status.imageCount} Images` : status.fileType?.toUpperCase()}
              </Typography>
            </Grid>
          </Grid>
          {mode === 'create' && <div className={classes.uploadActions}>{renderChangeFilesButton()}</div>}
        </div>
      );
    }

    if (imageFileType === 'any') {
      return (
        <Grid container justifyContent="flex-start">
          <Button variant="outlined" onClick={() => onOpenSelectImageUpload()} color="primary" component="span">
            Upload Files
          </Button>
        </Grid>
      );
    }

    return (
      <Grid container justifyContent="flex-start">
        <input onChange={(event) => handleFileChange(event, DCM)} multiple directory="" webkitdirectory="" moxdirectory="" hidden id="raised-button-study" type="file" />
        <label htmlFor="raised-button-study">
          <Button variant="outlined" component="span" color="primary">
            Upload Files
          </Button>
        </label>
      </Grid>
    );
  };

  const renderReportField = () => {
    if (!status.reportUploaded) {
      return (
        <Grid container justifyContent="flex-start">
          <Button onClick={onOpenUploadReportDialog} variant="outlined" component="span" color="primary">
            Upload Report
          </Button>
        </Grid>
      );
    }

    return (
      <div>
        <Grid container className={classes.statsContainer}>
          <Grid item xs={12}>
            {status.uploadedReport.map((file) => (
              <Typography className={classes.statsText}> {file.name} </Typography>
            ))}
          </Grid>
        </Grid>
        <div className={classes.uploadActions}>
          <input onChange={handleReportChange} hidden id="raised-button-report-change" type="file" accept="application/pdf" />
          <label htmlFor="raised-button-report-change">
            <span style={{ color: '#2AAAE2', cursor: 'pointer' }}>Change</span>
          </label>
          &nbsp; | &nbsp;
          <span onClick={onRemoveReport} style={{ color: '#B12A2A', cursor: 'pointer' }} aria-hidden="true">
            Remove
          </span>
        </div>
      </div>
    );
  };

  return (
    <Grid container>
      <Grid item style={{ height: '100%', minWidth: 'max(40%, 400px)', padding: '0 3rem' }}>
        <Typography className="field-label text-left mt-1 mb-1">
          Files <span style={{ color: '#B12A2A' }}>*</span>
        </Typography>
        {renderUploadField()}
      </Grid>

      <Grid item style={{ height: '100%', padding: '0 3rem' }}>
        <Typography className="field-label text-left mt-1 mb-1">
          Official Radiology Report {radiologyReportRequired ? <span style={{ color: '#B12A2A' }}>*</span> : null}
        </Typography>
        {renderReportField()}
      </Grid>
    </Grid>
  );
};
